import { useI18N } from "@sundae/react-hooks";
import { Text, AssetSelectTrigger as ToolkitAssetSelectTrigger } from "@sundae/ui-toolkit";
import { IAssetSelectTriggerProps } from "@sundae/ui-toolkit/dist/types/components/molecules/AssetSelectTrigger/AssetSelectTrigger";
import { IMappedAsset, useWalletBalance } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { FC, useCallback } from "react";

import { Asset } from "../../gql/generated/stats2.sdk";
import useAppNavigate from "../../hooks/router/useAppNavigate";
import { TAssetSelectContext, useAssetSelect } from "../../hooks/useAssetSelect";
import useIsLPAsset from "../../hooks/useIsLPAsset";
import { useIsWalletConnected } from "../../hooks/useIsWalletConnected";
import { Actions, useAppContext } from "../../stores";
import { ExchangeActions, useExchangeContext } from "../../stores/ExchangeContext";
import {
  EOrderTransactionBuilderState,
  OrderActions,
  useOrderContext,
} from "../../stores/OrderContext";
import { IAssetMetaData } from "../../types/Asset.types";
import { getAssetName } from "../../utils/assets.utils";
import AssetAvatar from "../AssetAvatar";
import AssetWithAssetName from "../AssetWithAssetName";
import AssetSelectDialog, {
  IAssetSelectDialogProps,
} from "../Dialogs/AssetSelectDialog/AssetSelectDialog";
import NoDataIndicator from "../NoDataIndicator";

export interface IAssetSelectProps extends Omit<IAssetSelectTriggerProps, "asset" | "emptyLabel"> {
  // Legacy conditional for old flows that don't have a context.
  // @TODO make required once old flows are updated
  activeAsset?:
    | AssetAmount<IAssetMetaData>
    | [AssetAmount<IAssetMetaData>, AssetAmount<IAssetMetaData>];
  assetName?: string; // Optional asset name to display in the trigger (only valid for multi-asset)
  assets: IMappedAsset<IAssetMetaData>[];
  context?: TAssetSelectContext;
  dialogTitle?: string;
  onAssetSelect?: IAssetSelectDialogProps["onAssetSelect"];
}

const AvatarComponent: FC<Pick<IAssetSelectProps, "activeAsset" | "assetName">> = ({
  activeAsset,
  assetName,
}) => {
  if (!activeAsset) return null;

  if (Array.isArray(activeAsset)) {
    return (
      <AssetWithAssetName
        assets={activeAsset.map((asset) => asset.metadata) as [Asset, Asset]}
        assetNameSize="sm"
        assetName={
          <Text tag="span" weight="semibold" size="sm">
            {assetName ? assetName : getAssetName(activeAsset[0]?.metadata)}
          </Text>
        }
      />
    );
  }

  return (
    <>
      {activeAsset?.metadata && <AssetAvatar asset={activeAsset?.metadata} size="sm" />}
      <Text size="sm" tag="span" weight="semibold">
        {getAssetName(activeAsset?.metadata)}
      </Text>
    </>
  );
};

const AssetSelectTrigger: FC<IAssetSelectProps> = ({
  activeAsset,
  assets,
  assetName,
  isDisabled = false,
  dialogTitle,
  context,
  onAssetSelect,
  isLoading,
  ...assetSelectTriggerProps
}) => {
  const { t } = useI18N("exchange");
  const isWalletConnected = useIsWalletConnected();
  const { dispatch: exchangeContextDispatch } = useExchangeContext();
  const navigate = useAppNavigate();
  const { handleAssetSelect } = useAssetSelect();
  const {
    state: {
      assets: { taken, given },
      derived: { transaction },
      flowData: { syncSearch, fetchingPool },
    },
    dispatch,
  } = useOrderContext();
  const {
    dispatch: appContextDispatch,
    state: {
      dialogs: { assetSelect },
    },
  } = useAppContext();
  const { isLPAsset } = useIsLPAsset();

  const isMultiAsset = Array.isArray(activeAsset);
  const assetWarning = isMultiAsset
    ? activeAsset.find((asset) => asset?.metadata?.warning)?.metadata?.warning
    : activeAsset?.metadata?.warning;

  const { query: balanceQuery } = useWalletBalance();

  const handleAssetSelectDialogClose = useCallback(() => {
    ExchangeActions.setCurrentPageAssetList(0, exchangeContextDispatch);
    Actions.setDialogState(
      { dialog: "assetSelect", isOpen: false, context: undefined },
      appContextDispatch
    );
  }, [appContextDispatch, exchangeContextDispatch]);

  const memoizedOnAssetSelect = useCallback(
    async (asset: IMappedAsset<IAssetMetaData>) => {
      handleAssetSelectDialogClose();

      // Legacy support for old flows.
      if (onAssetSelect) {
        return onAssetSelect(asset);
      }

      if (!context) {
        return;
      }

      handleAssetSelect({ context, assetMetadata: asset.metadata });
      given &&
        given.amount > 0n &&
        OrderActions.setGivenAmount(
          given.withAmount(0n),
          dispatch,
          "AssetSelectTrigger.memoizedOnAssetSelect.first"
        );
    },
    [
      onAssetSelect,
      navigate,
      context,
      taken,
      given,
      dispatch,
      handleAssetSelectDialogClose,
      syncSearch,
    ]
  );

  return (
    <>
      <ToolkitAssetSelectTrigger
        isLoading={isLoading || fetchingPool}
        {...assetSelectTriggerProps}
        assetComponent={
          activeAsset && <AvatarComponent activeAsset={activeAsset} assetName={assetName} />
        }
        emptyLabel={t("select", {
          context: assetSelectTriggerProps.isCompact ? "token" : "asset",
          ns: "common",
        })}
        onClick={() => {
          if (transaction?.state === EOrderTransactionBuilderState.BUILD) return;
          Actions.setDialogState(
            { dialog: "assetSelect", isOpen: true, context },
            appContextDispatch
          );
        }}
        isDisabled={isDisabled}
        {...(assetWarning && {
          warning: assetWarning,
        })}
      />

      <AssetSelectDialog
        activeAsset={isMultiAsset ? activeAsset[0] : activeAsset}
        assets={assets.filter(({ metadata }) => !isLPAsset(metadata.policyId))}
        copy={t("assetSelectDialog.copy")}
        isLoading={balanceQuery.isLoading && isWalletConnected}
        onAssetSelect={memoizedOnAssetSelect}
        open={assetSelect.isOpen && assetSelect.context === context}
        onClose={handleAssetSelectDialogClose}
        renderNoSearchResults={
          <NoDataIndicator containerClassName="mt-auto mb-auto justify-center" context="assets" />
        }
        searchPlaceholder={t("assetSelectDialog.searchPlaceholder")}
        title={dialogTitle ?? t("assetSelectDialog.title")}
      />
    </>
  );
};

export default AssetSelectTrigger;
